import { ref, reactive } from 'vue';
import { showToast } from 'vant';
import constants from '@/constants';
import { i18n } from '@/local/index';
import { Bridge } from '@/utils/bridge';
import imageLink from '@/configs/imageLink';
import { addQueryToUrl } from '@/utils/uri';
import { clickEvents } from '@/configs/awsRumConfig';
import { awsRumHandler } from '@/utils/awsRumHandler.js';
import { addFavoritesList, setCartDataChangeStatus } from '@/utils/common';
const { t } = i18n.global;

const {
  ADD_CART_AND_FAVORITES_STATUS,
  SHARE_MODULE: {
    SHARE_TYPE,
    WEBAPP_PROD_SUBDOMAIN,
    shareRouter: { DETAIL_PAGE }
  }
} = constants.Common;

const { SUCCESS_ICON_IMAGE_URL } = imageLink;

const isShowShareApp = ref(false);

const shareConfiguration = ref({});

const selectProductParams = reactive({
  showAddCart: false,
  selectedAsin: '',
  selectedMerchantId: '',
  selectedCount: '',
  isProductReplace: false
});

export const cartTabsUtilsFun = (cartData, isLoading) => {
  // Share goods
  const onShareItem = asinItem => {
    awsRumHandler.recordClickEvent({
      ...clickEvents.cartPage.shareProductInCart_btn,
      asinid: asinItem.asin
    });
    const shareUrl = addQueryToUrl(
      `${WEBAPP_PROD_SUBDOMAIN}/${DETAIL_PAGE}/${asinItem.asin}`,
      { isShare: true }
    );
    shareConfiguration.value = {
      asinInfo: {
        title: asinItem.title,
        imageUrls: [asinItem.mainImageUrl],
        ourPrice: asinItem.ourPriceObject || { ourPrice: asinItem.ourPrice },
        merchant_badge: asinItem.gsBadge.name ?? ''
      },
      shareUrl,
      type: SHARE_TYPE.shareImage
    };
    isShowShareApp.value = true;
  };

  // Remove individual items
  const onDeleteItem = asinId => {
    awsRumHandler.recordClickEvent({
      ...clickEvents.cartPage.removeFromCart_btn,
      asinid: asinId
    });
    Bridge.callFn(
      'deleteShoppingCartList',
      {
        asins: [asinId]
      },
      () => {
        cartData.value = cartData.value.filter(item => {
          return item.asin !== asinId;
        });
        // Mark the shopping cart data change status every time there is a change in the shopping cart data
        setCartDataChangeStatus(true);
        showToast({
          message: t('modules.cart.DELETE_SUCCESSFULLY'),
          icon: SUCCESS_ICON_IMAGE_URL,
          overlay: false,
          overlayClass: 'a-overlay-white',
          className: 'a-toast'
        });
      }
    );
  };

  // The Select SKU pop-up window opens
  const onSelectProduct = item => {
    awsRumHandler.recordClickEvent({
      ...clickEvents.cartPage.addAnotherVariant_btn,
      asinid: item.asin
    });
    Object.assign(selectProductParams, {
      selectedAsin: item.asin,
      selectedMerchantId: item.merchantId,
      selectedCount: item.count,
      showAddCart: true,
      isProductReplace: item.isReplace
    });
  };

  // Add favorites
  const onAddToFavorite = ({ asin, merchantId }) => {
    isLoading.value = true;
    addFavoritesList(asin, merchantId, data => {
      isLoading.value = false;
      if (data.result === ADD_CART_AND_FAVORITES_STATUS.SUCCESSFUL) {
        const index = cartData.value.findIndex(item => item.asin === asin);
        if (index >= 0) {
          cartData.value[index].isFavorite = true;
        }
        // Mark the shopping cart data change status every time there is a change in the shopping cart data
        setCartDataChangeStatus(true);
        showToast({
          message: t('modules.cart.COLLECT_SUCCESSFULLY'),
          icon: SUCCESS_ICON_IMAGE_URL,
          overlay: false,
          overlayClass: 'a-overlay-white',
          className: 'a-toast'
        });
      } else {
        showToast({
          message: t('modules.cart.COLLECT_FAILED'),
          icon: SUCCESS_ICON_IMAGE_URL,
          overlay: false,
          overlayClass: 'a-overlay-white',
          className: 'a-toast'
        });
      }
    });
  };
  // remove favorites
  const onRemoveFavorite = ({ asin }) => {
    isLoading.value = true;
    Bridge.callFn('deleteFavoriteItems', { asins: [asin] }, () => {
      isLoading.value = false;
      const index = cartData.value.findIndex(item => item.asin === asin);
      if (index >= 0) {
        cartData.value[index].isFavorite = false;
      }
      // Mark the shopping cart data change status every time there is a change in the shopping cart data
      setCartDataChangeStatus(true);
      showToast({
        message: t('modules.cart.REMOVE_SUCCESSFULLY'),
        icon: SUCCESS_ICON_IMAGE_URL,
        overlay: false,
        overlayClass: 'a-overlay-white',
        className: 'a-toast'
      });
    });
  };

  // Update the cached data status after the countdown ends
  const onCountDownFinish = asin => {
    const cartItemIndex = cartData.value.findIndex(item => item.asin === asin);
    cartData.value[cartItemIndex].dealOffer = {};
  };

  return {
    cartTabsState: {
      isShowShareApp,
      shareConfiguration,
      selectProductParams
    },
    handleCartTabsState: {
      onShareItem,
      onDeleteItem,
      onSelectProduct,
      onAddToFavorite,
      onRemoveFavorite,
      onCountDownFinish
    }
  };
};
